import { createRouter, createWebHistory } from 'vue-router'
import { getToken } from '@/utils/auth.js'
import { getMenusByUser } from '@/api/admin/menu.js'
import _ from 'lodash'
import store from '@/store'
import { treeToList } from '@/utils/util.js'
import { getUserRole } from '@/api/auth.js'
import i18n from '@/i18n'
import { isOverseas } from '@/utils/config.js'
import {
  DEFAULT_LOCALE,
  SUPPORTED_LANGS,
  LANG_TO_HTML,
  setSeoMeta,
  getLangFromRoute,
  buildSoftwareJsonLd,
  buildFaqJsonLd,
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
} from '@/utils/seo.js'

function resolveDefaultLang() {
  return isOverseas ? 'en' : DEFAULT_LOCALE
}

function setLangAndNext(to, next) {
  const lang = to.params.lang
  if (!SUPPORTED_LANGS.includes(lang)) {
    return next(`/${resolveDefaultLang()}`)
  }
  i18n.global.locale.value = lang
  localStorage.setItem('lang', lang)
  document.documentElement.lang = LANG_TO_HTML[lang] || 'en'
  applySeoForRoute(to)
  next()
}

function applySeoForRoute(route) {
  const key = route.meta?.seoKey
  if (!key) return

  const lang = getLangFromRoute(route)
  const pathKey = route.meta?.seoPath !== undefined ? route.meta.seoPath : route.path.split('/').filter(Boolean).slice(1).join('/')
  const t = i18n.global.t
  const tm = i18n.global.tm
  const title = t(`manga.seo.${key}.title`)
  const description = t(`manga.seo.${key}.description`)

  if (key === 'landing') {
    const faqItems = Array.isArray(tm('landing.faq.items')) ? tm('landing.faq.items') : []
    const software = buildSoftwareJsonLd({ lang, pathKey, title, description })
    const faq = buildFaqJsonLd(faqItems)
    setSeoMeta({
      title,
      description,
      lang,
      pathKey,
      type: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          ...software['@graph'],
          { ...faq, '@context': undefined },
        ],
      },
      jsonLdId: 'seo-jsonld',
    })
    return
  }

  if (key === 'faq') {
    const faqItems = tm('seoPage.faq.items')
    setSeoMeta({
      title,
      description,
      lang,
      pathKey,
      jsonLd: buildFaqJsonLd(Array.isArray(faqItems) ? faqItems : []),
      jsonLdId: 'seo-jsonld',
    })
    return
  }

  if (key === 'guide') {
    setSeoMeta({
      title,
      description,
      lang,
      pathKey,
      type: 'article',
      jsonLd: buildArticleJsonLd({ title, description, lang, pathKey }),
      jsonLdId: 'seo-jsonld',
    })
    return
  }

  setSeoMeta({
    title,
    description,
    lang,
    pathKey,
    jsonLd: buildBreadcrumbJsonLd(lang, pathKey, title),
    jsonLdId: 'seo-jsonld',
  })
}

const routes = [
  {
    path: '/',
    redirect: () => `/${resolveDefaultLang()}`,
    meta: { isWhite: true },
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)',
    name: 'Landing',
    component: () => import('@/views/Landing'),
    meta: { isWhite: true, seoKey: 'landing', seoPath: '' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/MangaTranslation',
    name: 'MangaTranslation',
    component: () => import('@/views/manga/MangaTranslation'),
    meta: { isWhite: true, seoKey: 'translate', seoPath: 'MangaTranslation' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/vip',
    name: 'MangaTranslationVip',
    component: () => import('@/views/manga/MangaTranslationVip'),
    meta: { isWhite: true, seoKey: 'vip', seoPath: 'vip' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/faq',
    name: 'Faq',
    component: () => import('@/views/seo/Faq.vue'),
    meta: { isWhite: true, seoKey: 'faq', seoPath: 'faq' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/guide',
    name: 'Guide',
    component: () => import('@/views/seo/Guide.vue'),
    meta: { isWhite: true, seoKey: 'guide', seoPath: 'guide' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/privacy',
    name: 'Privacy',
    component: () => import('@/views/seo/LegalPage.vue'),
    meta: { isWhite: true, seoKey: 'privacy', seoPath: 'privacy', legalType: 'privacy' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/agreement',
    name: 'Agreement',
    component: () => import('@/views/seo/LegalPage.vue'),
    meta: { isWhite: true, seoKey: 'agreement', seoPath: 'agreement', legalType: 'agreement' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/login',
    name: 'Login',
    component: () => import('@/views/login'),
    meta: { isWhite: true, seoKey: 'login', seoPath: 'login', robots: 'noindex,follow' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/resetPassword',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword'),
    meta: { isWhite: true, seoKey: 'resetPassword', seoPath: 'resetPassword', robots: 'noindex,follow' },
    beforeEnter: (to, from, next) => setLangAndNext(to, next),
  },
  {
    path: '/403',
    name: '403',
    component: () => import('@/views/403'),
    meta: { isWhite: true, robots: 'noindex,follow' },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/404'),
    meta: { isWhite: true, robots: 'noindex,follow' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.afterEach((to) => {
  if (to.meta?.robots?.includes('noindex')) {
    const lang = getLangFromRoute(to)
    setSeoMeta({
      title: i18n.global.t(`manga.seo.${to.meta.seoKey || 'login'}.title`),
      description: i18n.global.t(`manga.seo.${to.meta.seoKey || 'login'}.description`),
      lang,
      pathKey: to.meta.seoPath,
      robots: to.meta.robots,
    })
    return
  }
  if (to.meta?.seoKey) {
    applySeoForRoute(to)
  }
})

router.beforeEach(async (to, from, next) => {
  const tokenStr = getToken()
  let target = to.path.replace(/\/+$/, '') || '/'
  const whiteListPaths = getWhiteListPaths()

  if (!_.isEmpty(to.meta.parentUrl)) {
    target = to.meta.parentUrl
  }

  const isWhiteListed = whiteListPaths.some((path) => {
    if (path.includes(':')) {
      const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
      return pattern.test(target)
    }
    return path === target
  })

  if (isWhiteListed) {
    next()
  } else {
    if (!tokenStr) {
      next('/404')
    } else {
      await store.commit('setUserInfo')
      await store.commit('setUserRole')
      const paths = getPaths()
      const userPaths = await getUserPaths()

      const hasPathAccess = paths.some((path) => {
        if (path.includes(':')) {
          const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
          return pattern.test(target)
        }
        return path === target
      })

      const hasUserPathAccess = userPaths.some((path) => {
        if (path.includes(':')) {
          const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
          return pattern.test(target)
        }
        return path === target
      })

      if (hasPathAccess && hasUserPathAccess) {
        next()
      } else if (hasPathAccess && !hasUserPathAccess) {
        next('/403')
      } else {
        next('/404')
      }
    }
  }
})

function getWhiteListPaths() {
  const whiteListPaths = []
  treeToList(routes, (route) => {
    if (route.meta && route.meta.isWhite) {
      whiteListPaths.push(route.path)
    }
  })
  return whiteListPaths
}

function getPaths() {
  const paths = []
  treeToList(routes, (route) => {
    paths.push(route.path)
  })
  return paths
}

async function getUserPaths() {
  try {
    const { data } = await getMenusByUser()
    return data.filter((menu) => menu.url).map((menu) => menu.url)
  } catch (error) {
    console.error('获取用户菜单失败:', error)
    return []
  }
}

export default router
